import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import {
  Layers, CheckSquare, Clock, AlertTriangle, HourglassIcon,
  UserCheck, ArrowRight, Sparkles, Users, FileDown,
} from 'lucide-react';

/**
 * Iter45 — My Design Desk. Personal, low-click, design-first landing
 * for Owner + DM + Design Ops + Team. NO financial values.
 *
 * Every row opens the existing record — this page is a re-projection
 * of Actions, Stage Sign-offs, and Master Projects — it never persists
 * anything.
 */
export default function MyDesignDeskPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [teamLoad, setTeamLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/design-desk')
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(e => { if (alive) { setError(e?.response?.data?.detail || 'Unable to load Design Desk'); setLoading(false); } });
    // Team-load peek (silent 403 for Team; that's fine).
    api.get('/design-desk/team-load')
      .then(r => { if (alive) setTeamLoad(r.data); })
      .catch(() => { /* team role → 403 → panel simply hides */ });
    return () => { alive = false; };
  }, []);

  const cardCount = data?.counts || {};

  return (
    <AppShell activePath="/design-desk">
      <div className="max-w-6xl mx-auto space-y-5" data-testid="design-desk-root">
        <Header user={user} />

        {loading && <div className="text-[hsl(var(--foreground-muted))] text-sm">Loading your desk…</div>}
        {error && (
          <div className="border rounded-md p-3 text-sm text-[hsl(var(--danger))]"
               style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
               data-testid="design-desk-error">{error}</div>
        )}

        {data && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="design-desk-kpi-strip">
              <Kpi label="My projects"          value={cardCount.my_projects}       icon={Layers}      testId="dd-kpi-projects" />
              <Kpi label="My next actions"      value={cardCount.my_next_actions}   icon={CheckSquare} testId="dd-kpi-next" />
              <Kpi label="Due this week"        value={cardCount.due_this_week}     icon={Clock}       testId="dd-kpi-week" />
              <Kpi label="Awaiting my sign-off" value={cardCount.awaiting_signoff}  icon={UserCheck}   testId="dd-kpi-signoff"
                    accent={cardCount.awaiting_signoff > 0} />
            </div>

            {/* Two-column layout on md+, stacked on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Left — Projects */}
              <div className="md:col-span-2 space-y-4">
                <ProjectsPanel projects={data.my_projects} />
                {teamLoad && teamLoad.rows && teamLoad.rows.length > 0 && (
                  <TeamLoadPanel rows={teamLoad.rows} />
                )}
              </div>
              {/* Right — Actions + Sign-offs */}
              <div className="space-y-4">
                <AwaitingSignoffPanel rows={data.awaiting_signoff} />
                <ActionsPanel title="Due today"        rows={data.due_today}       emptyHint="Nothing due today." testId="dd-list-due-today"       icon={Clock} />
                <ActionsPanel title="Overdue"          rows={data.overdue_actions} emptyHint="No overdue actions." testId="dd-list-overdue"        icon={AlertTriangle} accent="danger" />
                <ActionsPanel title="Blocked"          rows={data.blocked_actions} emptyHint="Nothing blocked."     testId="dd-list-blocked"        icon={HourglassIcon} />
                <ActionsPanel title="Waiting on client" rows={data.waiting_client} emptyHint="Nothing waiting."     testId="dd-list-waiting"        icon={Sparkles} />
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}


function Header({ user }) {
  const hour = new Date().getHours();
  const g = hour < 5 ? 'Working late'
         : hour < 12 ? 'Good morning'
         : hour < 17 ? 'Good afternoon'
         : hour < 21 ? 'Good evening'
         : 'Working late';
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <div className="text-[13px] text-[hsl(var(--foreground-muted))]">Design Desk</div>
        <h1 className="font-heading text-3xl mt-0.5" data-testid="design-desk-heading">
          {g}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
        </h1>
        <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">
          Your active design work, in one place. Every row opens the real record — nothing here is duplicated.
        </div>
      </div>
      <a href="/downloads/32december-os-design-team-quick-start-guide.pdf"
         target="_blank"
         rel="noopener noreferrer"
         download
         className="text-[12px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 whitespace-nowrap"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="design-desk-quick-start-pdf"
         title="Download the 4-page Design Team Quick Start Guide (PDF)">
        <FileDown size={13} /> Quick Start Guide
      </a>
    </div>
  );
}


function Kpi({ label, value, icon: Icon, testId, accent = false }) {
  return (
    <div data-testid={testId}
         className="border rounded-xl p-4 flex items-start justify-between"
         style={{ borderColor: 'hsl(var(--border))',
                  background: accent ? 'hsl(var(--accent-soft))' : 'hsl(var(--card))' }}>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
        <div className="font-heading text-3xl mt-1 tabular-nums">{value ?? 0}</div>
      </div>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center"
           style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground-muted))' }}>
        <Icon size={16} />
      </div>
    </div>
  );
}


function ProjectsPanel({ projects }) {
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="dd-projects-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b"
           style={{ borderColor: 'hsl(var(--border))' }}>
        <div>
          <div className="font-heading text-base">My active projects</div>
          <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
            Current stage and optional quality-check progress. Quality checks are advisory only — they do not affect sign-off.
          </div>
        </div>
        <Link to="/projects" className="text-[12.5px] text-[hsl(var(--foreground-muted))] hover:underline inline-flex items-center gap-1"
              data-testid="dd-projects-view-all">All projects <ArrowRight size={13} /></Link>
      </div>
      {projects.length === 0 ? (
        <div className="p-4 text-[13px] text-[hsl(var(--foreground-muted))]">
          You are not assigned to any active projects yet.
        </div>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {projects.map(p => <ProjectRow key={p.id} p={p} />)}
        </ul>
      )}
    </div>
  );
}


function ProjectRow({ p }) {
  const pct = p.qc_total_this_stage
    ? Math.round((p.qc_done_this_stage / p.qc_total_this_stage) * 100)
    : 0;
  return (
    <li className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 hover:bg-[hsl(var(--surface-1))]"
        data-testid={`dd-project-row-${p.id}`}>
      <div className="min-w-0 flex-1">
        <Link to={`/projects/${p.id}`}
              className="font-medium truncate block"
              data-testid={`dd-project-link-${p.id}`}
              style={{ color: 'hsl(var(--foreground))' }}>
          {p.project_name || '(untitled)'}
        </Link>
        <div className="text-[12px] text-[hsl(var(--foreground-muted))] truncate">
          {p.client || '—'}{p.code ? ` · ${p.code}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="chip chip-neutral text-[11px]" data-testid={`dd-project-stage-${p.id}`}>
          {p.current_stage_label}
        </span>
        <QcMini pct={pct} done={p.qc_done_this_stage} total={p.qc_total_this_stage}
                 testId={`dd-project-qc-${p.id}`} />
      </div>
    </li>
  );
}


function QcMini({ pct, done, total, testId }) {
  return (
    <div className="min-w-[110px]" data-testid={testId}>
      <div className="flex items-center justify-between text-[10.5px] text-[hsl(var(--foreground-muted))] mb-0.5">
        <span>QC</span>
        <span className="tabular-nums">{done} of {total}</span>
      </div>
      <div className="h-1.5 rounded bg-[hsl(var(--surface-2))] overflow-hidden">
        <div className="h-full rounded"
             style={{ width: `${pct}%`,
                       background: pct >= 100 ? 'hsl(var(--success))'
                                : pct >= 50 ? 'hsl(var(--info))'
                                : 'hsl(var(--border-strong))' }} />
      </div>
    </div>
  );
}


function AwaitingSignoffPanel({ rows }) {
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="dd-awaiting-signoff-panel">
      <div className="px-4 py-3 border-b flex items-center gap-2"
           style={{ borderColor: 'hsl(var(--border))' }}>
        <UserCheck size={14} className="text-[hsl(var(--foreground-muted))]" />
        <div className="font-heading text-sm">Stages awaiting my sign-off</div>
      </div>
      {rows.length === 0 ? (
        <div className="p-3 text-[12.5px] text-[hsl(var(--foreground-muted))]">Nothing waiting on you.</div>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {rows.map((r, i) => (
            <li key={`${r.project_id}-${r.stage}-${i}`}
                className="px-3 py-2 text-[13px] hover:bg-[hsl(var(--surface-1))]"
                data-testid={`dd-signoff-row-${r.project_id}-${r.stage}`}>
              <Link to={`/projects/${r.project_id}`} className="block">
                <div className="font-medium truncate">{r.project_name || r.project_id.slice(0, 8)}</div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                  {r.stage_label} · submitted {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


const ROLE_SHORT = {
  team: 'Team',
  operations: 'Design Ops',
  dm_creative: 'DM Creative',
  dm_technical: 'DM Technical',
  dm_architecture: 'DM Architecture',
  owner: 'Owner',
};

function TeamLoadPanel({ rows }) {
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="dd-team-load-panel">
      <div className="px-4 py-3 border-b flex items-center gap-2"
           style={{ borderColor: 'hsl(var(--border))' }}>
        <Users size={14} className="text-[hsl(var(--foreground-muted))]" />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm">Team load</div>
          <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
            Open actions per teammate across the projects you oversee — advisory peek, not an approval workflow.
          </div>
        </div>
        <span className="chip chip-neutral text-[10.5px] tabular-nums">{rows.length}</span>
      </div>
      <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
        {rows.map(r => (
          <li key={r.user_id}
              className="px-4 py-2.5 flex items-center gap-3 hover:bg-[hsl(var(--surface-1))]"
              data-testid={`dd-team-load-row-${r.user_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium truncate">
                {r.name || r.email || r.user_id.slice(0, 8)}
              </div>
              <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                {ROLE_SHORT[r.role] || r.role} · {r.email}
              </div>
            </div>
            <TeamLoadStat label="open"     value={r.open_actions}    tone="neutral" testId={`dd-team-load-open-${r.user_id}`} />
            <TeamLoadStat label="overdue"  value={r.overdue_actions} tone="danger"  testId={`dd-team-load-overdue-${r.user_id}`} />
            <TeamLoadStat label="blocked"  value={r.blocked_actions} tone="warn"    testId={`dd-team-load-blocked-${r.user_id}`} />
            <TeamLoadStat label="waiting"  value={r.waiting_client}  tone="info"    testId={`dd-team-load-waiting-${r.user_id}`} />
            <Link to={`/followups?tab=actions&owner=${r.user_id}`}
                  className="text-[hsl(var(--foreground-muted))]"
                  title={`Open ${r.name || r.email}'s actions in Follow-ups`}
                  data-testid={`dd-team-load-open-btn-${r.user_id}`}>
              <ArrowRight size={14} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TeamLoadStat({ label, value, tone, testId }) {
  const empty = !value;
  const color = empty
    ? 'hsl(var(--foreground-subtle))'
    : tone === 'danger' ? 'hsl(var(--danger))'
    : tone === 'warn'   ? 'hsl(var(--warning))'
    : tone === 'info'   ? 'hsl(var(--info))'
    :                     'hsl(var(--foreground))';
  return (
    <div className="w-14 text-center flex-none" data-testid={testId}>
      <div className="text-[15px] font-medium tabular-nums" style={{ color }}>{value ?? 0}</div>
      <div className="text-[9.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))]">{label}</div>
    </div>
  );
}




function ActionsPanel({ title, rows, emptyHint, testId, icon: Icon, accent }) {  return (
    <div className="border rounded-xl bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={testId}>
      <div className="px-4 py-3 border-b flex items-center gap-2"
           style={{ borderColor: 'hsl(var(--border))' }}>
        {Icon && <Icon size={14} className={accent === 'danger' ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--foreground-muted))]'} />}
        <div className="font-heading text-sm">{title}</div>
        <span className="chip chip-neutral text-[10.5px] tabular-nums ml-auto">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-3 text-[12.5px] text-[hsl(var(--foreground-muted))]">{emptyHint}</div>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {rows.slice(0, 6).map(a => (
            <li key={a.id} className="px-3 py-2 text-[13px] hover:bg-[hsl(var(--surface-1))]"
                data-testid={`${testId}-row-${a.id}`}>
              <Link to={`/projects/${a.project_id}#actions`} className="block">
                <div className="truncate">{a.title}</div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] truncate">
                  {a.project_name || '(project)'} · {a.due_date || 'no due date'}{a.blocker ? ` · ${a.blocker}` : ''}
                </div>
              </Link>
            </li>
          ))}
          {rows.length > 6 && (
            <li className="px-3 py-2 text-[11.5px] text-[hsl(var(--foreground-muted))]">
              +{rows.length - 6} more · <Link to="/followups?tab=actions" className="underline">open in Follow-ups</Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
